import React, { useState } from 'react'
import { graphql, usePaginationFragment } from 'react-relay/hooks'
import {
  Text,
  Anchor,
  Table as Tables,
  TablePagination,
  FlexRow,
  LinkButton,
  Dropdown,
  Icon,
  FlexColumn,
} from 'elements'
import CompaniesTableRow from './CompaniesTableRow'
import { useModals, useMountEffect, useAuth } from 'hooks'
import EmptyState from 'components/common/EmptyState'
import CreateCompanyModal from '../create/CreateCompanyModal'
import FirstClientModalContainer from '../create/FirstClientModalContainer'

const { Table, Head, HeadRow, HeadCell, Body, Row, Cell } = Tables.Card

export default function CompaniesTable({ organization }) {
  const [includeDeleted, setIncludeDeleted] = useState(false)
  const { openModal } = useModals()
  const { currentOrg, freeTrial } = useAuth()
  const handleCreateCompany = () => openModal(CreateCompanyModal, {}, 'full')

  const { data, refetch, ...paginationProps } = usePaginationFragment(
    graphql`
      fragment CompaniesTable_organization on Organization
        @argumentDefinitions(
          cursor: { type: "Cursor" }
          count: { type: "Int", defaultValue: 10 }
          includeDeleted: { type: "IncludeDeletedOption", defaultValue: NO }
        )
        @refetchable(queryName: "CompaniesTablePaginationQuery") {
        companies(
          after: $cursor
          first: $count
          orderBy: CREATED_AT_DESC
          includeDeleted: $includeDeleted
        ) @connection(key: "CompaniesTable_companies") {
          edges {
            node {
              id
              deletedAt
              ...CompaniesTableRow_company
              ...CompanyContextMenu_company
            }
          }
        }
      }
    `,
    organization,
  )

  const edges = data?.companies?.edges || []
  const noCompanies = edges.length === 0
  const orgSubscription = currentOrg?.organizationSubscriptions?.nodes?.[0]
  const activeSubscription = orgSubscription?.status === 'active'
  const customBillingPlan = orgSubscription && orgSubscription?.billingPlan?.slug !== 'team-monthly'

  useMountEffect(() => {
    if (noCompanies) {
      openModal(FirstClientModalContainer, {}, 'full')
    }
  })

  // If the user is on a free account, figure out the one row that isn't going to be disabled
  const freeAccount = !(freeTrial || activeSubscription || customBillingPlan)

  let freeCompanyIndex = null
  if (freeAccount) {
    edges.forEach((e, i) => {
      if (!e.node.deletedAt && freeCompanyIndex === null) {
        freeCompanyIndex = i
      }
    })
  }

  const toggleArchived = () => {
    const inc = !includeDeleted
    setIncludeDeleted(inc)

    refetch({ includeDeleted: inc ? 'YES' : 'NO' }, { fetchPolicy: 'store-and-network' })
  }

  return (
    <Table spacing="large">
      <Head>
        <HeadRow>
          <HeadCell>Client Name</HeadCell>
          <HeadCell width="150px" />
          <HeadCell width="150px" />
          <HeadCell width="150px" />
          <HeadCell width="180px">Date Created</HeadCell>
          <HeadCell width="100px" />
          <HeadCell width="100px">
            <FlexRow alignItems="center" justifyContent="flex-end">
              <Dropdown
                position="bottom right"
                options={[
                  {
                    label: includeDeleted ? 'Hide archived' : 'Show archived',
                    onSelect: toggleArchived,
                  },
                ]}
                stopPropagation
                renderButton={() => (
                  <LinkButton kind="transparent">
                    <Icon
                      size="medium"
                      name="cog"
                      color={includeDeleted ? 'secondary.0' : 'neutral.2'}
                    />
                  </LinkButton>
                )}
              />
            </FlexRow>
          </HeadCell>
        </HeadRow>
      </Head>
      <Body>
        {noCompanies ? (
          <Row>
            <Cell colSpan={7}>
              <FlexColumn p="xxxlarge">
                <EmptyState
                  message="Get started by adding your first client!"
                  action={
                    <Text color="neutral.0">
                      <Anchor onClick={handleCreateCompany}>Create a client</Anchor> or{' '}
                      <Anchor href="https://www.scrutinize.io/howitworks" target="_blank">
                        learn how it works.
                      </Anchor>
                    </Text>
                  }
                />
              </FlexColumn>
            </Cell>
          </Row>
        ) : (
          edges.map(({ node: company }, i) => (
            <CompaniesTableRow
              disabled={freeAccount ? i !== freeCompanyIndex : false}
              company={company}
              key={company.id}
              isLast={i === edges.length - 1}
            />
          ))
        )}
      </Body>
      <TablePagination Table={Tables.Card} colSpan={7} {...paginationProps} />
    </Table>
  )
}
